import { useId, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import type { WebsiteEditRegion, WebsitePageKind } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PublishFlow, PublishIssueRow } from '../hooks/use-publish-flow';
import { useWebsiteT, type WebsiteT } from '../hooks/use-website-t';
import { readinessIssueKey, readinessWarningKey } from '../lib/issue-messages';
import type { PublishConfirmCopy } from '../lib/publish-copy';
import { regionName } from '../lib/region-labels';
import { PublishAdsNotice } from './publish-ads-notice';

export interface PublishDialogProps {
  readonly flow: PublishFlow;
  readonly onGoToItem: (
    region: WebsiteEditRegion,
    issuePageKind: WebsitePageKind | undefined,
  ) => void;
  readonly onGoToSettings: () => void;
  readonly onViewLive: () => void;
  /** B1: the way to the store publish flow, offered when the store is not public. */
  readonly onGoToStorePublish: () => void;
  /**
   * B1: whether the store itself is published. When it is not, the success
   * stage says the design goes live with the store and points to publishing
   * the store instead of a live store that is not there. Unknown (loading)
   * keeps the plain success.
   */
  readonly storePublished?: boolean;
  /**
   * Epic #228 P3: the question for this publish (`publishConfirmCopy`): which
   * design shoppers will see and what it replaces. Without it the dialog asks
   * the generic question.
   */
  readonly confirmCopy?: PublishConfirmCopy;
}

type GoToItem = PublishDialogProps['onGoToItem'];

interface IssueRowsProps {
  readonly rows: readonly PublishIssueRow[];
  /** `named`: the row has a region name to put in the sentence. */
  readonly messageKey: (code: PublishIssueRow['issue']['code'], named: boolean) => string;
  readonly t: WebsiteT;
  readonly onGoToItem: GoToItem;
  readonly onGoToSettings: () => void;
  readonly id?: string;
  readonly testId?: string;
}

/** One sentence per issue, each with a way to go where it can be fixed. */
function IssueRows({
  rows,
  messageKey,
  t,
  onGoToItem,
  onGoToSettings,
  id,
  testId,
}: IssueRowsProps): React.JSX.Element {
  return (
    <ul id={id} className="space-y-2" data-testid={testId}>
      {rows.map(({ issue, region }, index) => {
        const label = region ? regionName(t, region) : '';
        return (
          <li
            key={`${issue.code}-${issue.slotId ?? index}`}
            className="border-border flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm"
          >
            <span className="text-start">{t(messageKey(issue.code, label !== ''), { label })}</span>
            {issue.code === 'missing_store_logo' ? (
              <Button type="button" variant="outline" size="sm" onClick={onGoToSettings}>
                {t('admin.websiteTemplates.publish.goToSettings')}
              </Button>
            ) : region ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onGoToItem(region, issue.pageKind)}
              >
                {t('admin.websiteTemplates.readiness.goTo')}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

interface PublishWarningsProps {
  readonly rows: readonly PublishIssueRow[];
  readonly t: WebsiteT;
  readonly onGoToItem: GoToItem;
  readonly onGoToSettings: () => void;
}

/** Epic #228 P1: what is worth fixing, collapsed; it never stops the publish. */
function PublishWarnings({
  rows,
  t,
  onGoToItem,
  onGoToSettings,
}: PublishWarningsProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  return (
    <section
      data-testid="publish-warnings"
      data-expanded={expanded}
      className="border-border space-y-3 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-start text-sm font-semibold">
          {t('admin.websiteTemplates.publish.warningsTitle')}
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          data-testid="publish-warnings-toggle"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded
            ? t('admin.websiteTemplates.publish.warningsToggleHide')
            : t('admin.websiteTemplates.publish.warningsToggle')}
          <ChevronDown
            aria-hidden="true"
            className={
              expanded ? 'size-4 rotate-180 transition-transform' : 'size-4 transition-transform'
            }
          />
        </Button>
      </div>
      {expanded ? (
        <IssueRows
          id={listId}
          rows={rows}
          messageKey={readinessWarningKey}
          t={t}
          onGoToItem={onGoToItem}
          onGoToSettings={onGoToSettings}
          testId="publish-warnings-list"
        />
      ) : null}
    </section>
  );
}

/**
 * Publish, in one dialog: checking (Epic #228 P4: after the auto-save queue
 * is paused and flushed),
 * what to fix first (each item with a way to go there), the question (with
 * what is only worth fixing), the result. Errors stay in the dialog.
 */
export function PublishDialog({
  flow,
  onGoToItem,
  onGoToSettings,
  onViewLive,
  onGoToStorePublish,
  storePublished,
  confirmCopy,
}: PublishDialogProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  if (flow.stage === 'closed') return null;

  const busy = flow.stage === 'checking' || flow.stage === 'publishing';
  const hasWarnings = flow.stage === 'confirm' && flow.warnings.length > 0;
  // B1: the design is live, the store is not public yet. Say one thing and
  // point to the next step, never to a "store unavailable" page.
  const storeOffline = flow.stage === 'success' && storePublished === false;
  const logoNeeded =
    storeOffline && flow.warnings.some(({ issue }) => issue.code === 'missing_store_logo');
  const title =
    flow.stage === 'success'
      ? t('admin.websiteTemplates.publish.successTitle')
      : (confirmCopy?.title ?? t('admin.websiteTemplates.publish.title'));
  const question = confirmCopy?.body ?? t('admin.websiteTemplates.publish.body');

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // A publish on its way cannot be dismissed: its result is the answer.
        if (!next && !busy) flow.close();
      }}
    >
      {/* The dialog never outgrows the viewport: header and footer stay, the body scrolls. */}
      <DialogContent
        data-testid="publish-dialog"
        data-stage={flow.stage}
        className="flex max-h-[calc(100dvh-2rem)] flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {flow.stage === 'checking' ? (
              t('admin.websiteTemplates.publish.checking')
            ) : flow.stage === 'issues' ? (
              t('admin.websiteTemplates.publish.fixFirst')
            ) : storeOffline ? (
              <span data-testid="publish-store-hint">
                {t('admin.websiteTemplates.editor.publishedStoreHint')}
              </span>
            ) : flow.stage === 'success' ? (
              t('admin.websiteTemplates.editor.published')
            ) : hasWarnings && confirmCopy === undefined ? (
              t('admin.websiteTemplates.publish.warningsIntro')
            ) : (
              <span data-testid="publish-question">{question}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="publish-dialog-body"
          className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1 empty:hidden"
        >
          {logoNeeded ? (
            <p
              data-testid="publish-store-logo-note"
              className="text-muted-foreground text-start text-sm"
            >
              {t('admin.websiteTemplates.readiness.missingLogo')}
            </p>
          ) : null}

          {busy ? (
            <div
              role="status"
              data-testid="publish-progress"
              className="text-muted-foreground flex items-center gap-2 text-sm"
            >
              <Loader2 aria-hidden="true" className="size-4 motion-safe:animate-spin" />
              {flow.stage === 'checking'
                ? t('admin.websiteTemplates.publish.checking')
                : t('admin.websiteTemplates.editor.publishing')}
            </div>
          ) : null}

          {flow.stage === 'issues' ? (
            <IssueRows
              rows={flow.issues}
              messageKey={readinessIssueKey}
              t={t}
              onGoToItem={onGoToItem}
              onGoToSettings={onGoToSettings}
              testId="publish-issues"
            />
          ) : null}

          {/* Epic #228 P7: a legacy store's active ads stop showing; informs, never blocks. */}
          {flow.stage === 'confirm' ? <PublishAdsNotice /> : null}

          {hasWarnings && confirmCopy !== undefined ? (
            <p className="text-muted-foreground text-start text-sm">
              {t('admin.websiteTemplates.publish.warningsIntro')}
            </p>
          ) : null}
          {hasWarnings ? (
            <PublishWarnings
              rows={flow.warnings}
              t={t}
              onGoToItem={onGoToItem}
              onGoToSettings={onGoToSettings}
            />
          ) : null}

          {flow.stage === 'error' && flow.errorKey !== null ? (
            <p role="alert" data-testid="publish-error" className="text-destructive text-sm">
              {t(flow.errorKey)}
            </p>
          ) : null}
        </div>

        <DialogFooter className="shrink-0">
          {flow.stage === 'success' ? (
            <>
              <Button type="button" variant="outline" onClick={flow.close}>
                {t('admin.websiteTemplates.common.close')}
              </Button>
              {storeOffline ? (
                <Button
                  type="button"
                  data-testid="publish-go-to-store-publish"
                  onClick={onGoToStorePublish}
                >
                  {t('admin.websiteTemplates.publish.goToStorePublish')}
                </Button>
              ) : (
                <Button type="button" onClick={onViewLive}>
                  {t('admin.websiteTemplates.publish.viewLive')}
                </Button>
              )}
            </>
          ) : flow.stage === 'confirm' ? (
            <>
              <Button type="button" variant="outline" onClick={flow.close}>
                {t('admin.websiteTemplates.common.cancel')}
              </Button>
              <Button
                type="button"
                data-testid="publish-confirm"
                onClick={() => void flow.confirm()}
              >
                {hasWarnings
                  ? t('admin.websiteTemplates.publish.publishAnyway')
                  : t('admin.websiteTemplates.publish.confirm')}
              </Button>
            </>
          ) : flow.stage === 'error' ? (
            <>
              <Button type="button" variant="outline" onClick={flow.close}>
                {t('admin.websiteTemplates.common.close')}
              </Button>
              <Button type="button" data-testid="publish-retry" onClick={() => void flow.retry()}>
                {t('admin.websiteTemplates.common.retry')}
              </Button>
            </>
          ) : flow.stage === 'issues' ? (
            <Button type="button" variant="outline" onClick={flow.close}>
              {t('admin.websiteTemplates.common.close')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
